import {useContext, useEffect, useRef, useState} from "react";
import {useMessages} from "i18n";
import {ToastContext} from "Toast";
import {useUser} from "UserProvider";
import {
	renderedFailureMessage,
	reportRenderedFailure,
	RenderedFailureKey,
} from "utils/renderedFailure";
import {useKeyedSingleFlight} from "utils/useSingleFlight";
import {canUseCustomerBranding, planOf} from "home/gating";
import {
	categoryGroups, isPageThemeTemplate, PAGE_THEME_TEMPLATES, pageThemeCategories, SPONSOR_TEMPLATES,
	type RenderedPageThemeKey, type SponsorOptions, type TemplateCategoryGroup,
} from "sponsor/sponsorBooking";
import {useHalloweenSlidesOn} from "home/landing/useHalloweenCampaign";
import {
	adoptedSponsorOptions,
	draftSponsorOptions,
	withSponsorOption,
	type SponsorOptionErrors,
	type SponsorOptionKey,
} from "sponsor/sponsorOptions";
import {compressPickedImage} from "core/images/compressPickedImage";
import {admitPickedFiles, processPickedBatch} from "core/images/pickedImageBatch";
import {ImageCompressionError} from "core/images/imageCompression";
import {BACKDROP_IMAGE_TARGET} from "core/images/uploadImageTargets";
import {AccountThemeApi, type PageTheme} from "../AccountThemeApi";
import {
	canDeleteImage,
	deselectImage,
	selectImage,
	selectedImages,
	tilesMerged,
	tilesOf,
	type ThemeTile,
} from "../brand/themeLibrary";
import {
	classifyThemeUploadFailure,
	themeImagesPublished,
	themeOptionsForWire,
	themeSettingErrors,
	MAX_THEME_IMAGES,
	ThemeImageRejected,
	type ThemeUploadFailureMessage,
	validateThemeDraft,
	validateThemeImage,
} from "../brand/themeValidation";

/** One picked image the batch could not add, named so the sender knows WHICH file failed. */
export interface ThemeUploadFailure {
	/** The picked file's own name. Sender-supplied text, echoed rather than translated. */
	name: string;
	/** Catalog selector resolved by the view through whichever locale is active when it renders. */
	message: ThemeUploadFailureMessage;
}

/**
 * What one multi-file pick did (ui#664), or `null` when it added everything it was given.
 *
 * Three independent outcomes, because a batch has no single verdict: images that never had a slot,
 * images the library or the server refused, and — when a pick arrives while another batch is still
 * running — images that were not taken at all.
 */
export interface ThemeUploadReport {
	/**
	 * Which REPORTED pick this is, counting from 1. A pick that lands cleanly produces no report and does
	 * not advance it, so it counts problems rather than picks.
	 *
	 * Carried so the view can key on it: two picks can produce a byte-identical report, and without
	 * something that changes React renders identical text into the live region, mutating no DOM — leaving
	 * a screen-reader user never told their retry failed too.
	 */
	reportId: number;
	/** Picks dropped because a batch was already in flight. Zero for a pick that actually ran. */
	busy: number;
	/** Picks dropped for want of a slot in the library. */
	refused: number;
	/** The images that were admitted but could not be added, in pick order. */
	failures: readonly ThemeUploadFailure[];
}

/**
 * Contract of {@link useAccountPageTheme} — the thin React adapter over `PageThemeService`
 * (api#511). Every rule it applies lives in `account/brand/themeValidation.ts`; this hook only sequences
 * requests and surfaces toasts (epic #26).
 */
export interface UseAccountPageTheme {
	/** Whether the plan includes customer branding. `false` renders the upsell instead of the editor. */
	entitled: boolean;
	/** The theme recipients see right now, or an empty placeholder until the first load resolves. */
	saved: PageTheme;
	/**
	 * The treatment in the picker — the saved one until the sender moves it.
	 *
	 * Narrowed to what the picker can OFFER rather than to everything the contract names: the served
	 * enum carries VIDEO_LOOP, the api refuses it on a page theme, and a card for it would have no copy
	 * and no renderer. {@link adopt} maps anything outside the catalogue back to the default.
	 */
	template: RenderedPageThemeKey;
	/**
	 * The picker's labelled groups: the scenes only while the Halloween campaign's slides are on, by the transfer
	 * composer's rule and switch (`pageThemeCategories`, owner decision 2026-10-27, kitecorp/transferify-ui#3776).
	 * A theme already saved as a scene keeps rendering; only what the picker OFFERS follows the switch.
	 */
	templateGroups: readonly TemplateCategoryGroup<RenderedPageThemeKey>[];
	/**
	 * The wallpaper settings the sender is composing (kitecorp/transferify-ui#1869).
	 *
	 * Held for EVERY treatment, backdrop included, exactly as the booking draft holds them: a sender who
	 * tries a wallpaper, looks at a carousel and comes back must find their layout where they left it.
	 * What the write actually sends is `themeOptionsForWire`'s answer, which is `null` for a backdrop.
	 */
	options: SponsorOptions;
	/** Which settings the api would refuse, keyed by control. Empty while the draft is publishable. */
	optionErrors: SponsorOptionErrors;
	/** Whether the draft would be switched on when published. */
	enabled: boolean;
	/**
	 * The sender's whole image LIBRARY, selected images first in the order they will animate (api#471).
	 *
	 * Every image the account owns is here, not only the ones the live theme renders, so closing and
	 * reopening the sheet no longer strands the rest. Each tile carries both the saved `attached` flag and
	 * the draft `selected` one; the chosen treatment publishes as many SELECTED images as it takes, from
	 * the front, so this is not the list a recipient sees — `saved` is.
	 */
	images: readonly ThemeTile[];
	/** True while a request is in flight; the editor disables its controls. */
	busy: boolean;
	/** True while an image is uploading, which is the only busy state with its own label. */
	uploading: boolean;
	/** True when the working set reaches the treatment's minimum and can therefore be published. */
	canSave: boolean;
	/** True when there is a saved theme to remove. */
	canRemove: boolean;
	setTemplate: (template: RenderedPageThemeKey) => void;
	/** Sets one wallpaper setting, or clears it with `undefined`. */
	setOption: <K extends SponsorOptionKey>(key: K, value: SponsorOptions[K] | undefined) => void;
	setEnabled: (enabled: boolean) => void;
	/**
	 * Adds every picked image that fits the LIBRARY cap, in pick order (ui#664).
	 *
	 * Takes a list because one dialog should fill the library; a single pick is a batch of one. Never
	 * rejects — a refused or corrupt image is reported through {@link uploadReport} and the rest still
	 * land.
	 */
	uploadImages: (images: readonly File[]) => Promise<void>;
	/** What the last multi-file pick did, or `null` when it added everything it was given. */
	uploadReport: ThemeUploadReport | null;
	/** Puts an image into the draft theme. Local until Publish. */
	useImage: (assetId: string) => void;
	/** Takes an image out of the draft theme, KEEPING it in the library. Local until Publish. */
	unuseImage: (assetId: string) => void;
	/**
	 * Deletes an image from the library for good — a real request, unlike the other three above. Refused by
	 * the api while the image is part of a live theme; {@link deletableImages} says which ones qualify, so
	 * the sheet disables rather than offers-and-fails.
	 */
	deleteImage: (assetId: string) => Promise<void>;
	/** The ids the api would currently accept a delete for. */
	deletableImages: ReadonlySet<string>;
	save: () => Promise<void>;
	remove: () => Promise<void>;
}

/** The treatment offered first — the same order the sponsor picker uses. */
const DEFAULT_TEMPLATE: RenderedPageThemeKey = SPONSOR_TEMPLATES[0];

const EMPTY: PageTheme = {enabled: false, template: null, options: null, images: []};

export function useAccountPageTheme(): UseAccountPageTheme {
	const m = useMessages();
	const [user] = useUser();
	const {showErrorToast, showSuccessToast} = useContext(ToastContext);
	const [saved, setSaved] = useState<PageTheme>(EMPTY);
	const [template, setTemplate] = useState<RenderedPageThemeKey>(DEFAULT_TEMPLATE);
	// The whole draft, not a per-treatment map: a sender composes ONE poster and tries it under whichever
	// wallpaper they like, and `themeOptionsForWire` decides which of its keys a given treatment sends.
	const [options, setOptions] = useState<SponsorOptions>(() => draftSponsorOptions(null));
	const [enabled, setEnabled] = useState(false);
	const [images, setImages] = useState<readonly ThemeTile[]>([]);
	const [busy, setBusy] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [uploadReport, setUploadReport] = useState<ThemeUploadReport | null>(null);
	// A ref, not state: nothing renders this number, it only has to DIFFER between two reports so the
	// alert remounts and is announced again. Held across renders, or every pick would reuse one value.
	const reports = useRef(0);
	const nextReportId = () => {
		reports.current += 1;
		return reports.current;
	};
	// Survives the batch that caused it. A pick refused for being mid-flight is reported immediately, but
	// the batch already running finishes AFTER that and writes its own report — which would erase the
	// only record that those files were never taken. Carried here and folded into that final report.
	const droppedWhileBusy = useRef(0);
	const entitled = canUseCustomerBranding(planOf(user));
	// Read only for a sender the picker is shown to, through the page's one memoised read.
	const slidesOn = useHalloweenSlidesOn(entitled);

	// Load once. Non-entitled senders skip the request entirely: the read is ungated, but there is
	// nothing for them to edit and a request behind an upsell is noise. Same shape as `useAccountBrand`.
	useEffect(() => {
		if (!entitled) {
			return;
		}
		let active = true;
		(async () => {
			try {
				const loaded = await AccountThemeApi.getTheme();
				if (!active) {
					return;
				}
				adopt(loaded);
			} catch (e) {
				showErrorToast(renderedFailureMessage(
					e, RenderedFailureKey.LoadFailed, m.errors, "account_theme_load"));
			}
		})();
		return () => {
			active = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entitled]);

	/**
	 * Adopts a server response as the new truth, images included — every response now carries the sender's
	 * whole library, so there is nothing left for the editor to preserve on its own (api#471).
	 *
	 * This used to take an override that only a SAVE passed (ui#583). A save publishes the slice of the
	 * working set its treatment animates, and the response came back holding ONLY that slice, so adopting
	 * it would have taken a sender who published Hero from three images down to one in the same instant
	 * they pressed Publish. Keeping the local list instead rescued them — but only until the sheet closed,
	 * because `BrandSheet` is a MUI modal with `keepMounted` off and the next open re-read a payload that
	 * still could not name the unpublished images. The read is the fix; the workaround is gone with it,
	 * and with it the risk of the editor and the server disagreeing about what is attached.
	 */
	function adopt(theme: PageTheme) {
		setSaved(theme);
		setTemplate(offeredTemplate(theme));
		// A theme saved under a wallpaper opens on its own look, `null` included; only a theme with no
		// wallpaper yet starts from the default pair (kitecorp/transferify-ui#2416). See `adoptedSponsorOptions`.
		setOptions(adoptedSponsorOptions(theme.template, theme.options));
		setEnabled(theme.enabled);
		setImages(tilesOf(theme));
	}

	/**
	 * Adopts the response to a WRITE. Same refresh, except the sender's unpublished SELECTION survives it:
	 * the server has no notion of selection, so re-deriving it from `attached` would drop the images a
	 * narrower treatment did not take out of the draft the moment Publish landed.
	 */
	function adoptWrite(theme: PageTheme) {
		setSaved(theme);
		setTemplate(offeredTemplate(theme));
		/*
		 * The SETTINGS are deliberately not re-adopted here, for the reason the selection is not: the
		 * response carries only what the saved treatment stores, so publishing a carousel would answer
		 * `null` and wipe the poster a sender had just composed for the wipe they were about to try. What
		 * the server holds is in `saved.options`; the draft is the sender's.
		 */
		setEnabled(theme.enabled);
		setImages(current => tilesMerged(theme, current));
	}

	/**
	 * The saved treatment as the PICKER can hold it: its own, or the default when there is none — and
	 * also when the api names one this catalogue does not offer.
	 *
	 * The last case is not hypothetical bookkeeping. `PageTheme.template` is bound to the served enum,
	 * which lists every treatment, the scenes with no painter here included; a theme naming one the picker has no card for would leave every
	 * radio unchecked with no way to select anything, and the live line above them reading a name out of
	 * an object that has none. Falling back is the same fail-closed move `toRecipientTheme` makes.
	 */
	function offeredTemplate(theme: PageTheme): RenderedPageThemeKey {
		return theme.template !== null && isPageThemeTemplate(theme.template)
			? theme.template
			: DEFAULT_TEMPLATE;
	}

	// `busy` disables the controls, but only a render after the click. Keys are per ACTION so publishing
	// never blocks a removal (ui#360).
	const flight = useKeyedSingleFlight();

	const run = (
		key: string,
		operation: "account_theme_delete" | "account_theme_remove" | "account_theme_save",
		request: () => Promise<PageTheme>,
		success: string,
		/** Runs only after a write that LANDED, so a rejected one changes nothing. */
		onWritten?: () => void,
	): Promise<void> => flight.run(key, async () => {
		setBusy(true);
		try {
			// Only a write that actually landed reaches this, so a rejected one changes nothing locally.
			adoptWrite(await request());
			onWritten?.();
			showSuccessToast(success);
		} catch (e) {
			showErrorToast(renderedFailureMessage(
				e, RenderedFailureKey.SaveFailed, m.errors, operation));
		} finally {
			setBusy(false);
		}
	});

	const uploadImages = async (picked: readonly File[]) => {
		if (picked.length === 0) {
			return;
		}
		// Asked BEFORE the gate, not left to it. `flight.run` hands a second caller the first one's
		// promise and never invokes its action, so a pick made while a batch is still running would
		// otherwise be discarded in silence — with a drop target that is a real gesture, and the sender
		// would watch their files vanish. Synchronous, so two picks in one tick still cannot both start.
		if (flight.activeFor("upload")) {
			droppedWhileBusy.current += picked.length;
			setUploadReport({
				reportId: nextReportId(),
				busy: droppedWhileBusy.current,
				refused: 0,
				failures: [],
			});
			return;
		}
		// The cap is the LIBRARY's, not the chosen treatment's: this surface is a library with a
		// published slice (ui#583), so an image beyond the treatment's arity is still worth keeping —
		// `themeImagesPublished` clamps at publish time. The sponsor booking page caps by arity instead,
		// because a booking is a one-shot submission against one treatment.
		const {admitted, refused} = admitPickedFiles(picked, images.length, MAX_THEME_IMAGES);
		await flight.run("upload", async () => {
			setUploadReport(null);
			setUploading(true);
			try {
				const report = await processPickedBatch(admitted, async image => {
					const verdict = validateThemeImage(image);
					if (!verdict.valid) {
						throw new ThemeImageRejected(verdict.error);
					}
					// Inside the busy flag, because shrinking a phone photo is the slow part of this action
					// and the sender should see the sheet working for all of it (ui#601).
					const ready = await compressPickedImage(image, BACKDROP_IMAGE_TARGET);
					return AccountThemeApi.uploadImage(ready);
				});
				// Selected on arrival: a sender who has just picked images means to use them, and the api
				// has not attached them to anything yet, so `attached` is false until the next Publish.
				// One append for the whole batch, so the row changes once rather than per file.
				if (report.succeeded.length > 0) {
					setImages(current => [
						...current,
						...report.succeeded.map(uploaded => ({...uploaded, attached: false, selected: true})),
					]);
				}
				const failures = report.failures.map(failure => {
					const classified = classifyThemeUploadFailure(failure.error);
					if (classified.diagnostic !== null) {
						reportRenderedFailure("account_theme_upload_item", {diagnostic: classified.diagnostic});
					}
					return {name: failure.picked.name, message: classified.message};
				});
				const busy = droppedWhileBusy.current;
				droppedWhileBusy.current = 0;
				// `nextReportId()` is called HERE rather than inside a state updater: an updater is
				// double-invoked under StrictMode, which would burn an id and skip a remount.
				setUploadReport(refused === 0 && failures.length === 0 && busy === 0
					? null
					: {reportId: nextReportId(), busy, refused, failures});
			} catch (e) {
				// `processPickedBatch` cannot reject, so reaching here means the fault was OURS — resolving a
				// result or reading a catalog. Keep the caller's settled-promise contract while presenting only
				// locale-owned copy and a reduced diagnostic.
				showErrorToast(e instanceof ImageCompressionError
					? m.account.theme.errors.imageUnprocessable
					: renderedFailureMessage(
						e, RenderedFailureKey.SaveFailed, m.errors, "account_theme_upload"));
			} finally {
				setUploading(false);
			}
		});
	};

	const useImage = (assetId: string) => setImages(current => selectImage(current, assetId));

	const unuseImage = (assetId: string) => setImages(current => deselectImage(current, assetId));

	/**
	 * Keyed per IMAGE, not per action, so deleting one tile never disables the others: each delete is its
	 * own request and they are independent (ui#360's reason for a keyed flight in the first place).
	 *
	 * The response is the whole remaining library, so it is adopted exactly like any other write.
	 */
	const deleteImage = async (assetId: string) => {
		await run(`delete:${assetId}`, "account_theme_delete", () => AccountThemeApi.deleteImage(assetId),
			m.account.theme.toasts.imageDeleted, retireOverflow);
	};

	/**
	 * Drops the "didn't fit" half of a report once a slot is actually free, keeping any per-file failure.
	 *
	 * Deleting is the remedy that message asked for, so leaving it up would tell the sender there is no
	 * room in the same breath as making room. `unuseImage` deliberately does NOT do this: taking an image
	 * out of the theme keeps it in the library and frees nothing. "bad.svg was not a PNG" also stays true
	 * however many images are deleted, so only the overflow count is retired.
	 */
	function retireOverflow() {
		setUploadReport(current => {
			if (current === null || current.refused === 0) {
				return current;
			}
			return current.failures.length === 0 ? null : {...current, refused: 0};
		});
	}

	const save = async () => {
		const chosen = selectedImages(images);
		const verdict = validateThemeDraft(template, chosen.length);
		if (!verdict.valid) {
			showErrorToast(m.account.theme.errors[verdict.error]);
			return;
		}
		// The treatment's own slice of the SELECTED images, never the whole library: the api rejects a count
		// outside the template's arity outright (`BackdropTemplate.accepts`). This is the same slice the
		// preview beside the button is already animating.
		const published = themeImagesPublished(template, chosen);
		await run(
			"save",
			"account_theme_save",
			() => AccountThemeApi.saveTheme({
				template,
				enabled,
				// `null` for a backdrop, and only the keys this treatment takes for a wallpaper: the api
				// refuses a setting the treatment does not carry, so the draft's leftovers stay local.
				options: themeOptionsForWire(template, options),
				assetIds: published.map(image => image.assetId),
			}),
			m.account.theme.toasts.saved,
		);
	};

	const remove = async () => {
		// The cleared response carries the library back with every image unattached, so the sender keeps
		// what they uploaded and only the decoration goes. Before api#471 it carried nothing at all, and
		// adopting it emptied the editor of images the account still owned.
		await run("remove", "account_theme_remove", () => AccountThemeApi.removeTheme(), m.account.theme.toasts.removed);
	};

	// Through the shared helper since kitecorp/transferify-api-service#1149: the spread this used to be
	// stored `undefined` UNDER the key instead of removing it, which every reader that counts keys would
	// have read as a setting the sender had chosen.
	const setOption: UseAccountPageTheme["setOption"] = (key, value) =>
		setOptions(current => withSponsorOption(current, key, value));

	// Computed ONCE and read twice: the editor shows these against their controls, and `canSave` is the
	// same question asked of the whole set. Two calls could not disagree today, but they are two places
	// for the button and the message under a control to stop meaning the same thing.
	const optionErrors = themeSettingErrors(template, options);

	return {
		entitled,
		saved,
		template,
		templateGroups: categoryGroups(PAGE_THEME_TEMPLATES, pageThemeCategories(slidesOn)),
		options,
		optionErrors,
		enabled,
		images,
		busy,
		uploading,
		canSave: validateThemeDraft(template, selectedImages(images).length).valid
			&& Object.keys(optionErrors).length === 0,
		canRemove: saved.template !== null,
		setTemplate,
		setOption,
		setEnabled,
		uploadImages,
		uploadReport,
		useImage,
		unuseImage,
		deleteImage,
		deletableImages: new Set(images
			.filter(image => canDeleteImage(image, saved.enabled))
			.map(image => image.assetId)),
		save,
		remove,
	};
}
